import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import style from "./InsuranceBlock.module.scss";

export default function InsuranceModal({ title, description, onClose, busy, children, arranging = false }) {
  const { t } = useTranslation();
  const titleId = useId();
  const dialog = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("input, button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const handleKeys = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (!busy) onClose();
    }
    if (event.key !== "Tab") return;
    const elements = dialog.current.querySelectorAll("button:not(:disabled), input:not(:disabled), a[href]");
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (!first) event.preventDefault();
    else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className={style.overlay} data-portal-modal="true" onClick={() => !busy && onClose()}>
      <div
        ref={dialog}
        className={`${style.modal} ${arranging ? style.arranging : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy}
        tabIndex={-1}
        onKeyDown={handleKeys}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={style.modalHeader}>
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className={style.iconButton}
            onClick={onClose}
            disabled={busy}
            aria-label={t("common.close")}
          >
            <X size={22} />
          </button>
        </div>
        {description && <p className={style.description}>{description}</p>}
        {children}
      </div>
    </div>,
    document.body,
  );
}
